import React, { useState } from 'react';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const EyeIcon = ({ open, onClick }) => (
  <button type="button" onClick={onClick} data-testid="toggle-password" style={{
    position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
    color: 'rgba(196, 137, 74, 0.50)', fontSize: '16px', lineHeight: 1,
  }}>
    {open ? (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
      </svg>
    ) : (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
    )}
  </button>
);

const selectStyle = {
  width: '100%',
  padding: '14px 16px',
  background: 'rgba(255, 243, 225, 0.04)',
  border: '0.5px solid rgba(255, 243, 225, 0.12)',
  borderRadius: '8px',
  color: 'rgba(255, 243, 225, 0.82)',
  fontSize: '15px',
  fontFamily: "'EB Garamond', Georgia, serif",
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer',
};

const RegisterView = ({ onRegister, onNavigate }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [dobYear, setDobYear] = useState('');
  const [error, setError] = useState('');

  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const passwordsMismatch = confirmPassword && password !== confirmPassword;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const daysInMonth = dobMonth && dobYear
    ? new Date(parseInt(dobYear), parseInt(dobMonth), 0).getDate()
    : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const buildDob = () => {
    if (!dobMonth || !dobDay || !dobYear) return null;
    const m = String(dobMonth).padStart(2, '0');
    const d = String(dobDay).padStart(2, '0');
    return `${dobYear}-${m}-${d}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!email || !password) {
      setError('Please provide email and password');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const dob = buildDob();
    if (!dob) {
      setError('Please enter your date of birth');
      return;
    }
    // Client-side 18+ check
    const dobDate = new Date(dob);
    const ageDays = (Date.now() - dobDate.getTime()) / (1000 * 60 * 60 * 24);
    if (ageDays < 18 * 365) {
      setError('You must be 18 or older to use Eunoia.');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          name: name.trim(),
          date_of_birth: dob,
          beta_participant_id: localStorage.getItem('beta_participant_id') || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Registration failed');
        return;
      }
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_email', data.email);
      localStorage.setItem('user_id', data.user_id);
      if (data.display_name) localStorage.setItem('user_name', data.display_name);
      onRegister();
    } catch (err) {
      setError('Could not connect to server');
    }
  };

  return (
    <div className="velvet-void" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '0 32px',
    }} data-testid="register-view">
      <div style={{ width: '100%', maxWidth: '400px' }} className="fade-in">
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div className="breathing-sphere" style={{ margin: '0 auto' }} />
        </div>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 className="font-serif" style={{ fontSize: '2.25rem', color: 'rgba(255, 243, 225, 0.82)', marginBottom: '8px' }}>Eunoia Presence</h1>
          <p style={{ color: 'rgba(255, 243, 225, 0.35)', fontSize: '0.875rem' }}>Create your space</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && (
            <div style={{ textAlign: 'center', fontSize: '0.875rem', color: 'rgba(196, 137, 74, 0.8)' }}>{error}</div>
          )}

          <input type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="register-name" className="velvet-input" required />
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email" className="velvet-input" required />

          {/* Date of birth */}
          <div>
            <p style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '13px',
              color: 'rgba(255, 243, 225, 0.28)',
              marginBottom: '8px',
              paddingLeft: '2px',
              letterSpacing: '0.04em',
            }}>
              date of birth
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr', gap: '8px' }}>
              <div style={{ position: 'relative' }}>
                <select
                  value={dobMonth}
                  onChange={(e) => setDobMonth(e.target.value)}
                  data-testid="register-dob-month"
                  style={selectStyle}
                >
                  <option value="" disabled style={{ background: '#080608' }}>Month</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i + 1} style={{ background: '#080608' }}>{m}</option>
                  ))}
                </select>
              </div>
              <div style={{ position: 'relative' }}>
                <select
                  value={dobDay}
                  onChange={(e) => setDobDay(e.target.value)}
                  data-testid="register-dob-day"
                  style={selectStyle}
                >
                  <option value="" disabled style={{ background: '#080608' }}>Day</option>
                  {days.map(d => (
                    <option key={d} value={d} style={{ background: '#080608' }}>{d}</option>
                  ))}
                </select>
              </div>
              <div style={{ position: 'relative' }}>
                <select
                  value={dobYear}
                  onChange={(e) => setDobYear(e.target.value)}
                  data-testid="register-dob-year"
                  style={selectStyle}
                >
                  <option value="" disabled style={{ background: '#080608' }}>Year</option>
                  {years.map(y => (
                    <option key={y} value={y} style={{ background: '#080608' }}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div style={{ position: 'relative' }}>
            <input type={showPassword ? 'text' : 'password'} placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="velvet-input"
              style={{ paddingRight: '44px' }} />
            <EyeIcon open={showPassword} onClick={() => setShowPassword(!showPassword)} />
          </div>

          <div>
            <div style={{ position: 'relative' }}>
              <input type={showConfirm ? 'text' : 'password'} placeholder="Confirm password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} data-testid="register-confirm-password"
                className="velvet-input" style={{
                  paddingRight: '44px',
                  borderColor: passwordsMatch ? 'rgba(100, 180, 100, 0.35)' : passwordsMismatch ? 'rgba(196, 137, 74, 0.25)' : undefined,
                }} />
              <EyeIcon open={showConfirm} onClick={() => setShowConfirm(!showConfirm)} />
            </div>
            {passwordsMismatch && (
              <p style={{
                fontFamily: "'EB Garamond', Georgia, serif", fontSize: '13px',
                color: 'rgba(196, 137, 74, 0.55)', marginTop: '6px', paddingLeft: '4px',
              }} data-testid="password-mismatch">passwords do not match yet</p>
            )}
          </div>

          <button type="submit" className="velvet-btn" data-testid="register-submit"
            disabled={!!(confirmPassword && password !== confirmPassword)}
            style={{ marginTop: '8px', opacity: (confirmPassword && password !== confirmPassword) ? 0.4 : 1 }}>
            Create account
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '0.75rem', color: 'rgba(255, 243, 225, 0.25)' }}>
          By continuing, you agree to our{' '}
          <button onClick={() => {
            onNavigate('about');
            setTimeout(() => {
              const el = document.getElementById('our-promise');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', color: 'rgba(255, 243, 225, 0.35)', fontSize: '0.75rem' }}>
            Promise
          </button>
        </p>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <button onClick={() => onNavigate('login')} data-testid="goto-login"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255, 243, 225, 0.35)', fontSize: '0.875rem' }}>
            Already have one? <span style={{ textDecoration: 'underline', color: 'rgba(255, 243, 225, 0.55)' }}>Log in</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterView;
